import { NotificationContext } from 'common/NotificationContainer/NotificationContainer';
import { evaluateTenantName } from 'common/TenantContainer/evaluateTenantName';
import {
  useLazyPostCall,
  useLazyPutCall,
} from 'common/api/useApiCall';
import { URLS } from 'constants/URL';
import { OTPRequestType } from 'constants/URLEnums';
import { OTP_TYPE } from 'constants/otpType';
import { TenantType } from 'customers/tenants-common-view/lib/schema/Tenant.types';
import { useContext, useEffect, useState } from 'react';

type useToggleFeatureFlagProps = {
  tenant: TenantType;
  onToggleSuccess?: () => void;
};

export const useToggleFeatureFlag = (props: useToggleFeatureFlagProps) => {
  const { tenant, onToggleSuccess } = props;
  const { _id, pgSource } = tenant;

  const [openOTP, setOpenOTP] = useState(false);
  const [otpType, setOtpType] = useState('');
  const [toggledFlag, setToggledFlag] = useState<any>(null);
  const [step, setStep] = useState(1);
  const [uuid, setUuid] = useState('');
  const { addNotification } = useContext(NotificationContext);

  const {
    response: sendOTPRes,
    isLoading: sendOTPIsLoading,
    postData: sendOTP,
  } = useLazyPostCall(`${URLS.otp}purpose=${otpType}`);

  const {
    response: toggleFeatureFlagResponse,
    postData: toggleFeatureFlagPostData,
    isLoading: toggleFeatureFlagIsLoading,
    responseStatus: toggleFeatureFlagResponseStatus,
  } = useLazyPutCall(
    `${URLS.updateFeatureFlag}?tenant-id=${_id}&tenant-domain=${pgSource || ''}`,
    null,
    true,
  );

  useEffect(() => {
    if (
      toggleFeatureFlagResponseStatus === 200 &&
      toggleFeatureFlagResponse?.message
    ) {
      addNotification({
        severity: 'success',
        message: `${toggleFeatureFlagResponse?.message}`,
      });
      if (onToggleSuccess) {
        onToggleSuccess();
      }
      setOpenOTP(false);
      setStep(1);
    } else if (
      toggleFeatureFlagResponseStatus &&
      toggleFeatureFlagResponseStatus !== 200
    ) {
      addNotification({
        severity: 'error',
        message: 'Something went wrong, please try again.',
      });
    }
    setOtpType('');
    setToggledFlag(null);
  }, [toggleFeatureFlagResponse, toggleFeatureFlagResponseStatus]);

  const sendOTPCall = ({ comment, jiraId }) => {
    const payload = {
      tenantId: _id,
      comment: comment,
      jiraId: jiraId,
      newFlagValue: toggledFlag?.status === 'ENABLED' ? false : true,
      flagName: toggledFlag?.name,
      featureId: toggledFlag?.featureId,
    };
    sendOTP(JSON.stringify(payload, null));
  };

  useEffect(() => {
    if (sendOTPRes?.uuid) {
      setUuid(sendOTPRes?.uuid);
      setStep(2);
    }
  }, [sendOTPRes]);

  const getTitleAndReason = () => {
    if (otpType === OTPRequestType.TENANT_FLAG_TOGGLE_OTP && toggledFlag) {
      return {
        title: `${toggledFlag.status === 'ENABLED' ? 'Disable' : 'Enable'} ${
          toggledFlag?.displayName || toggledFlag?.name
        } for ${evaluateTenantName({
          tenant: tenant,
        })}`,
        subTitle: null,
        reasonHeading:
          'Enter reason for changing the feature flag status.',
        otpType: OTP_TYPE.TENANT_FLAG_TOGGLE_OTP,
      };
    }
    return null;
  };

  const onSubmit = (val) => {
    if (step === 1) {
      sendOTPCall({
        comment: val?.reason,
        jiraId: val?.jiraId,
      });
    } else if (step === 2) {
      if (otpType === OTPRequestType.TENANT_FLAG_TOGGLE_OTP && toggledFlag) {
        const payload = {
          flagName: toggledFlag?.name,
          featureId: toggledFlag?.featureId,
          newFlagValue: toggledFlag?.status === 'ENABLED' ? false : true,
          requestId: uuid,
          otp: val,
        };
        toggleFeatureFlagPostData(JSON.stringify(payload));
      }
    }
  };

  return {
    openOTP,
    setOpenOTP,
    step,
    setStep,
    uuid,
    setUuid,
    onSubmit,
    otpType,
    setOtpType,
    setToggledFlag,
    sendOTPIsLoading,
    toggleFeatureFlagIsLoading,
    getTitleAndReason,
  };
};
